import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";

/* ────────────────────────────────────────────────────────────────
 * Shared building blocks for the 10 Age-Calculator sub-pages.
 * Additive – doesn't touch existing hub/sub-pages.
 * ──────────────────────────────────────────────────────────────── */

export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Combined SoftwareApplication + FAQPage JSON-LD (single @graph script). */
export function buildAppFaqLd(opts: {
  name: string;
  description: string;
  faqs: { q: string; a: string }[];
}) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          name: opts.name,
          operatingSystem: "All",
          applicationCategory: "UtilitiesApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          browserRequirements: "Requires JavaScript. Requires HTML5.",
          description: opts.description,
        },
        {
          "@type": "FAQPage",
          mainEntity: opts.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    }),
  };
}

/** Preset "quick" chips shown beneath the DOB input. */
export function PresetChips({
  presets,
  onPick,
  label = "Quick presets",
}: {
  presets: { label: string; value: string }[];
  onPick: (iso: string) => void;
  label?: string;
}) {
  if (!presets.length) return null;
  return (
    <div className="mt-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => onPick(p.value)}
            className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Life-expectancy progress bar – smooth animated fill. */
export function LifeProgressBar({ dob, expectancy = 73 }: { dob: string; expectancy?: number }) {
  const pct = useMemo(() => {
    if (!dob) return 0;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return 0;
    const years = (Date.now() - d.getTime()) / (365.25 * 86400000);
    return Math.max(0, Math.min(100, (years / expectancy) * 100));
  }, [dob, expectancy]);
  if (!dob) return null;
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-5">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-sm font-semibold">Life Progress Bar</div>
        <div className="text-xs text-muted-foreground">
          {pct.toFixed(1)}% of avg {expectancy}-yr expectancy
        </div>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-gradient-to-r from-primary via-primary/80 to-primary/50 transition-all duration-1500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground">
        Based on global average life expectancy. Individual outcomes vary with lifestyle, healthcare and genetics.
      </div>
    </div>
  );
}

/** Weekday grid for the next 5 birthdays. */
export function WeekdayMilestoneGrid({ dob, count = 5 }: { dob: string; count?: number }) {
  const rows = useMemo(() => {
    if (!dob) return [];
    const d = new Date(dob);
    if (isNaN(d.getTime())) return [];
    const now = new Date();
    const names = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const out: { year: number; age: number; date: string; weekday: string }[] = [];
    let year = now.getFullYear();
    // Next upcoming birthday
    const thisYearBd = new Date(year, d.getMonth(), d.getDate());
    if (thisYearBd < now) year += 1;
    for (let i = 0; i < count; i++) {
      const bd = new Date(year + i, d.getMonth(), d.getDate());
      const age = bd.getFullYear() - d.getFullYear();
      out.push({
        year: bd.getFullYear(),
        age,
        date: bd.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        weekday: names[bd.getDay()],
      });
    }
    return out;
  }, [dob, count]);
  if (!rows.length) return null;
  return (
    <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted">
          <tr>
            <th className="p-3 text-left font-semibold">Age</th>
            <th className="p-3 text-left font-semibold">Birthday</th>
            <th className="p-3 text-left font-semibold">Weekday</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.year} className="border-t border-border">
              <td className="p-3 font-semibold tabular-nums">{r.age}</td>
              <td className="p-3 tabular-nums">{r.date}</td>
              <td className="p-3">
                <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-semibold">
                  {r.weekday}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Named-anchor Table of Contents for mini-sitelinks. */
export function AnchorToc({ items }: { items: { id: string; label: string }[] }) {
  return (
    <nav
      aria-label="On this page"
      className="mx-auto max-w-4xl px-4 mt-8"
    >
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">On this page</div>
        <ul className="flex flex-wrap gap-2">
          {items.map((it) => (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className="inline-block rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:border-primary hover:text-primary transition"
              >
                {it.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

export function CopyResultButton({ text, label = "Copy result" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch {}
      }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:border-primary hover:text-primary transition"
    >
      {copied ? "✓ Copied" : `📋 ${label}`}
    </button>
  );
}

/** A single content card section – used to build the anchor-linked body. */
export function AnchorSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className="scroll-mt-24 mx-auto max-w-4xl px-4 py-8"
    >
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h2 className="text-xl md:text-2xl font-bold mb-3">{title}</h2>
        <div className="prose prose-sm md:prose-base dark:prose-invert max-w-none">{children}</div>
      </div>
    </section>
  );
}

/** Small helper: chronological Y/M/D diff between two ISO strings. */
export function chronoDiff(fromISO: string, toISO: string) {
  const a = new Date(fromISO);
  const b = new Date(toISO);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  const [from, to] = a <= b ? [a, b] : [b, a];
  let years = to.getFullYear() - from.getFullYear();
  let months = to.getMonth() - from.getMonth();
  let days = to.getDate() - from.getDate();
  if (days < 0) {
    months -= 1;
    days += new Date(to.getFullYear(), to.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const totalMs = Math.abs(b.getTime() - a.getTime());
  const totalDays = Math.floor(totalMs / 86400000);
  return { years, months, days, totalDays, forward: a <= b };
}

/** Live-ticking now – rerenders every second when enabled. */
export function useNowTick(active: boolean) {
  const [, setT] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setT((v) => v + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);
}